import React from 'react';
import { Event } from '../types';
import { deriveLifecycle } from '@shared/competitionRules';
import { formatClub, formatClubTimeCompact, getClubDateParts } from '../utils/clubTime';
import { downloadEventIcs } from '../utils/eventIcs';

/**
 * One row in the landing page's Upcoming Events list (#105 list view).
 *
 * Layout: date block | title, pill, one meta line, one price line | one
 * state-aware action. Below 640px the action drops under the text at full
 * width; the date block always stays on the left.
 */

export type UpcomingEvent = Event & {
  endDate?: string | null;
  registrationCloseTime?: string | null;
};

export type EventRowAction =
  | { kind: 'CHECK_IN' }
  | { kind: 'OPENS_SOON'; opensAt: Date }
  | { kind: 'IN_PROGRESS' }
  | { kind: 'ADD_TO_CALENDAR' };

/** How far ahead "Check-in opens ..." replaces "Add to calendar". Display only. */
const OPENS_SOON_LOOKAHEAD_MS = 24 * 60 * 60 * 1000;

/**
 * Picks the row's action from the shared lifecycle function
 * (`deriveLifecycle`) only. No window maths here. Check-in wins while sign-on
 * is open (7 to 9:10am on comp day); after that, a running event shows "in
 * progress" rather than a pointless "Add to calendar".
 */
export function getEventRowAction(event: UpcomingEvent, now: number = Date.now()): EventRowAction {
  const nowDate = new Date(now);
  if (event.eventType === 'COMPETITION') {
    const l = deriveLifecycle(event as any, nowDate);
    if (l.signOnOpen) return { kind: 'CHECK_IN' };
    if (l.signOnOpensAt && l.signOnOpensAt.getTime() > now && l.signOnOpensAt.getTime() - now <= OPENS_SOON_LOOKAHEAD_MS) {
      return { kind: 'OPENS_SOON', opensAt: l.signOnOpensAt };
    }
    // #113/#134: for a competition, "in progress" / "Submit a catch" must
    // agree with /catch (catchesOpen), which also stops once adjudicated,
    // cancelled or past the catch deadline.
    if (l.catchesOpen) return { kind: 'IN_PROGRESS' };
    return { kind: 'ADD_TO_CALENDAR' };
  }

  // Non-competition events: a simple start..end "in progress" window, no
  // lifecycle concepts apply.
  const start = eventStart(event);
  const end = eventEnd(event);
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : startMs;
  if (now >= startMs && now <= endMs) return { kind: 'IN_PROGRESS' };
  return { kind: 'ADD_TO_CALENDAR' };
}

export function eventStart(event: UpcomingEvent): string {
  return event.competitionStartTime || event.eventDate;
}

/**
 * When an event drops off the landing page (list and strip). Competitions
 * stay until the catch deadline (end + grace), so "Submit a catch" is there
 * as long as /catch accepts catches (#113/#134). Other events: their end,
 * else their start.
 */
export function eventListedUntil(event: UpcomingEvent): number {
  const end = eventEnd(event);
  if (event.eventType === 'COMPETITION' && end) {
    const l = deriveLifecycle(event as any, new Date());
    return l.catchDeadline ? l.catchDeadline.getTime() : new Date(end).getTime();
  }
  return new Date(end || event.eventDate).getTime();
}

export function eventEnd(event: UpcomingEvent): string | null {
  return event.competitionEndTime || event.endDate || null;
}

/** `Fishing 7am–2pm` for competitions, `7am–2pm` / `Starts 7am` otherwise. */
export function formatEventTimes(event: UpcomingEvent): string {
  const start = formatClubTimeCompact(eventStart(event));
  const endIso = eventEnd(event);
  const end = endIso ? formatClubTimeCompact(endIso) : '';
  const range = end ? `${start}–${end}` : `Starts ${start}`;
  return event.eventType === 'COMPETITION' ? `Fishing ${range}` : range;
}

const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** `Members $10 · Guests $15`, `Members $10`, or `Free`. */
export function formatEventPrice(event: UpcomingEvent): string {
  const cost = Number(event.cost) || 0;
  const guest = Number(event.nonMemberPrice) || 0;
  const parts: string[] = [];
  if (cost > 0) parts.push(`Members ${money(cost)}`);
  if (event.allowNonMembers && guest > 0) parts.push(`Guests ${money(guest)}`);
  if (parts.length) return parts.join(' · ');
  return event.allowNonMembers ? 'Free · Everyone welcome' : 'Free for members';
}

const PILL: Record<string, { label: string; className: string }> = {
  COMPETITION: { label: '🏆 Competition', className: 'bg-blue-100 text-blue-900' },
  SOCIAL: { label: '🎉 Social', className: 'bg-amber-100 text-amber-900' },
  MEETING: { label: '🗓️ Meeting', className: 'bg-gray-100 text-gray-800' },
  TRAINING: { label: '🎓 Training', className: 'bg-green-100 text-green-900' },
  TRIP: { label: '🚤 Trip', className: 'bg-teal-100 text-teal-900' },
};

interface UpcomingEventRowProps {
  event: UpcomingEvent;
  /** The next upcoming event: highlighted, and the only row with a blurb. */
  isNext: boolean;
  now?: number;
  onCheckIn?: () => void;
}

const ACTION_BASE =
  'inline-flex w-full sm:w-auto sm:min-w-[11rem] min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

interface EventActionProps {
  event: UpcomingEvent;
  now?: number;
  onCheckIn?: () => void;
  /**
   * During fishing hours, show a "Submit a catch" button (to /catch) instead
   * of the "Fishing now" status. Used by the next-event strip (#102); the
   * Upcoming Events list keeps the status.
   */
  catchDuringFishing?: boolean;
  onSubmitCatch?: () => void;
}

/**
 * The one state-aware action for an event, shared by UpcomingEventRow and
 * NextEventStrip (#102). State comes only from getEventRowAction.
 */
export const EventAction: React.FC<EventActionProps> = ({ event, now, onCheckIn, catchDuringFishing, onSubmitCatch }) => {
  const action = getEventRowAction(event, now ?? Date.now());
  const endIso = eventEnd(event);
  const endLabel = endIso ? formatClubTimeCompact(endIso) : '';

  if (action.kind === 'IN_PROGRESS' && catchDuringFishing && event.eventType === 'COMPETITION') {
    return (
      <button
        type="button"
        data-testid="event-action"
        onClick={onSubmitCatch ?? (() => { window.location.href = '/catch'; })}
        aria-label={`Submit a catch for ${event.title}`}
        className={`${ACTION_BASE} bg-green-700 text-white hover:bg-green-800 focus-visible:ring-green-700`}
      >
        🐟 Submit a catch
      </button>
    );
  }

  return (
    <>
      {action.kind === 'CHECK_IN' && (
        <button
          type="button"
          data-testid="event-action"
          onClick={onCheckIn ?? (() => { window.location.href = '/check-in'; })}
          aria-label={`Check in now for ${event.title}`}
          className={`${ACTION_BASE} bg-green-700 text-white hover:bg-green-800 focus-visible:ring-green-700`}
        >
          ✅ Check in now
        </button>
      )}
      {action.kind === 'OPENS_SOON' && (
        <div
          data-testid="event-action"
          role="status"
          aria-label={`Check-in for ${event.title} opens ${formatClub(action.opensAt, { weekday: 'long' })} ${formatClubTimeCompact(action.opensAt)}`}
          className={`${ACTION_BASE} cursor-default bg-gray-100 text-gray-700 ring-1 ring-gray-200`}
        >
          ⏳ Check-in opens {formatClub(action.opensAt, { weekday: 'short' })} {formatClubTimeCompact(action.opensAt)}
        </div>
      )}
      {action.kind === 'IN_PROGRESS' && (
        <div
          data-testid="event-action"
          role="status"
          aria-label={`${event.title} is in progress${endLabel ? `, entries close ${endLabel}` : ''}`}
          className={`${ACTION_BASE} cursor-default bg-amber-50 text-amber-900 ring-1 ring-amber-300`}
        >
          <span aria-hidden="true" className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75 motion-reduce:hidden" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-600" />
          </span>
          {event.eventType === 'COMPETITION'
            ? `Fishing now${endLabel ? ` · closes ${endLabel}` : ''}`
            : 'Happening now'}
        </div>
      )}
      {action.kind === 'ADD_TO_CALENDAR' && (
        <button
          type="button"
          data-testid="event-action"
          onClick={() =>
            downloadEventIcs({
              id: event.id,
              title: event.title,
              start: eventStart(event),
              end: eventEnd(event),
              location: event.address || event.location,
              description: event.shortDescription || event.description,
              url: 'https://www.tumbyfc.com/events',
            })
          }
          aria-label={`Add ${event.title} to calendar`}
          className={`${ACTION_BASE} bg-white text-fishing-blue ring-1 ring-fishing-blue hover:bg-blue-50 focus-visible:ring-fishing-blue`}
        >
          📅 Add to calendar
        </button>
      )}
    </>
  );
};

const UpcomingEventRow: React.FC<UpcomingEventRowProps> = ({ event, isNext, now, onCheckIn }) => {
  const parts = getClubDateParts(eventStart(event));
  const pill = PILL[event.eventType] ?? { label: String(event.eventType).replace('_', ' '), className: 'bg-gray-100 text-gray-800' };
  const blurb = isNext ? (event.shortDescription || event.description || '').trim() : '';
  const times = formatEventTimes(event);

  return (
    <li
      data-testid="event-row"
      className={`flex gap-3 sm:gap-5 rounded-xl p-4 md:p-5 transition-shadow ${
        isNext
          ? 'bg-blue-50 ring-1 ring-blue-200 shadow-md'
          : 'bg-white ring-1 ring-gray-200 shadow-sm hover:shadow-md'
      }`}
    >
      {/* Date block */}
      <div
        data-testid="event-date-block"
        role="img"
        aria-label={parts?.fullDate ?? ''}
        className={`flex w-16 sm:w-20 shrink-0 flex-col items-center justify-center self-start rounded-lg py-2 text-center whitespace-nowrap ${
          isNext ? 'bg-fishing-blue text-white' : 'bg-blue-50 text-fishing-blue ring-1 ring-blue-100'
        }`}
      >
        <span aria-hidden="true" className="text-xs font-bold tracking-widest">{parts?.monthShort}</span>
        <span aria-hidden="true" className="text-3xl font-extrabold leading-none my-0.5">{parts?.day}</span>
        <span aria-hidden="true" className="text-xs font-medium">{parts?.weekdayShort}</span>
      </div>

      {/* Text + action */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {isNext && (
              <span className="rounded-full bg-fishing-blue px-2 py-0.5 text-xs font-semibold text-white">Next up</span>
            )}
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${pill.className}`}>{pill.label}</span>
          </div>

          <h3
            data-testid="event-title"
            title={event.title}
            className="line-clamp-2 break-words text-base sm:text-lg font-semibold leading-snug text-gray-900"
          >
            {event.title}
          </h3>

          {/* One meta line. Below 640px it may wrap to two, but only between the
              time and the location, never mid-chunk; from 640px it is a single
              line and a long location truncates (full text in the tooltip). */}
          <p
            data-testid="event-meta"
            title={event.location ? `${times} · ${event.location}` : times}
            className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-sm text-gray-700 sm:flex-nowrap"
          >
            <span className="shrink-0 whitespace-nowrap font-medium">🕖 {times}</span>
            {event.location && (
              <>
                <span aria-hidden="true" className="hidden sm:inline">·</span>
                <span className="min-w-0 max-w-full truncate">📍 {event.location}</span>
              </>
            )}
          </p>

          <p data-testid="event-price" className="mt-0.5 truncate text-sm font-medium text-green-800">
            {formatEventPrice(event)}
          </p>

          {blurb && (
            <p data-testid="event-blurb" className="mt-2 text-sm text-gray-700 line-clamp-3">
              {blurb}
            </p>
          )}
        </div>

        <div className="sm:shrink-0">
          <EventAction event={event} now={now} onCheckIn={onCheckIn} />
        </div>
      </div>
    </li>
  );
};

export default UpcomingEventRow;
